import { useEffect, useRef, useState } from 'react';

/**
 * Generic localStorage-backed state. Callers supply stable (module-level)
 * serialize/deserialize functions; deserialize returns null to reject an
 * invalid/absent stored value and fall back. The write is skipped on the
 * initial mount (the value was just seeded from the same key), so navigating
 * between screens that share a key does not redundantly rewrite it.
 *
 * storageKey is expected to be stable for the hook instance's lifetime (as with
 * the sibling persisted hooks); the value is seeded from it once on mount and is
 * not re-read if the key later changes.
 */
export const usePersistedState = <T>(config: {
  storageKey: string;
  fallback: T;
  deserialize: (raw: string) => T | null;
  serialize: (value: T) => string;
}) => {
  const { storageKey, fallback, deserialize, serialize } = config;

  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return fallback;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return fallback;
      const parsed = deserialize(raw);
      return parsed === null ? fallback : parsed;
    } catch {
      return fallback;
    }
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    try {
      window.localStorage.setItem(storageKey, serialize(value));
    } catch {
      // ignore storage errors (e.g. disabled cookies/storage)
    }
  }, [storageKey, value, serialize]);

  return [value, setValue] as const;
};
